import type {
  AddShopProductClaimAttachmentDTO,
  BulkCreateExchangeRatesDTO,
  ConvertCurrencyDTO,
  CreateExchangeRateDTO,
  CreatePurchaseOrderDTO,
  CreateShopBranchDTO,
  CreateShopPayoutRequestDTO,
  CreateShopProductClaimDTO,
  CreateShopPromotionDTO,
  CreateSupplierDTO,
  ExchangeRateHistoryQueryDTO,
  InventoryAssignLocationDTO,
  InventoryByLocationQueryDTO,
  PosCheckoutDTO,
  PosCheckoutPreviewDTO,
  PosCreateHoldDTO,
  PosHoldConfirmDTO,
  PosSaleAddPaymentsDTO,
  PosSalesByCurrencyReportQueryDTO,
  PosSalesExtendedReportQueryDTO,
  PosSalesListQueryDTO,
  ProvisionPosSellableProductDTO,
  PublishMarketplaceListingDTO,
  PurchaseOrderGoodsReceiptDTO,
  PurchaseOrderListQueryDTO,
  SellableCatalogQueryDTO,
  SetShopProductClaimStatusDTO,
  ShopInventoryAdjustDTO,
  ShopInventoryGoodsReceiptDTO,
  ShopInventoryListQueryDTO,
  ShopInventoryLotLabelsQueryDTO,
  ShopInventoryMovementsQueryDTO,
  ShopInventoryReconcileQueryDTO,
  ShopInventoryReleaseDTO,
  ShopInventoryReserveDTO,
  ShopInventorySerialsQueryDTO,
  ShopMarketplaceListQueryDTO,
  ShopPayoutRequestsQueryDTO,
  ShopProductClaimListQueryDTO,
  ShopPromotionListQueryDTO,
  ShopSettlementPeriodsQueryDTO,
  ShopStockTransferDTO,
  ShopWarrantiesExpiringQueryDTO,
  SupplierListQueryDTO,
  UpdatePurchaseOrderDTO,
  UpdateShopBranchDTO,
  UpdateShopProductClaimDraftDTO,
  UpdateShopProductSkuDTO,
  UpdateShopPromotionDTO,
  UpdateShopSettingsDTO,
  UpdateSupplierDTO,
  WarehouseEmptyLocationsReportQueryDTO,
  WarehouseLayoutAssignZoneDTO,
  WarehouseLayoutBackgroundDTO,
  WarehouseLayoutFloorCreateDTO,
  WarehouseLayoutGenerateDTO,
  WarehouseLayoutNodesReplaceDTO,
  WarehouseLayoutUpsertDTO,
  WarehouseLocationCreateDTO,
  WarehouseLocationUpdateDTO,
  WarehouseReceiveByLocationReportQueryDTO,
  WarehouseUnlocatedReportQueryDTO,
  WarehouseZoneCreateDTO,
  WarehouseZoneUpdateDTO,
} from "@/modules/merchant/domain/contracts";
import type { ShopFeatureFlags } from "@/modules/merchant/domain/contracts/shop-feature-flags";
import type {
  CreateShopShiftAssignmentDTO,
  ShiftAssignmentsListQueryDTO,
  ShiftStaffOptionsQueryDTO,
  UpdateShopShiftAssignmentDTO,
} from "@/modules/merchant/domain/shift-assignments.contracts";
import type {
  CreateShopShiftSwapRequestDTO,
  ShopShiftSwapRequestsListQueryDTO,
} from "@/modules/merchant/domain/shift-swap.contracts";
import type { MerchantPermissionId } from "@/modules/roles/domain/contracts/merchant-permissions";
import type { ShopStaffListQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import type { ShopCurrency } from "@/shared/lib/currency";
import { fetcher } from "@/shared/lib/fetcher";
import { canUsePermissionWithLevel } from "../lib/shop-verification";

// ---------------------------------------------------------------------------
// Types — Shops (merchant context)
// ---------------------------------------------------------------------------

export type ShopVerifiedLevel = "none" | "basic" | "verified";
export type ShopStatus = "active" | "suspended" | "unverified";

export type EffectiveExchangeRateItem = {
  baseCurrency: ShopCurrency;
  quoteCurrency: ShopCurrency;
  rate: number;
  effectiveFrom: string;
  source: string;
  scope: "shop" | "global";
};

export type ExchangeRateHistoryItem = {
  id: string;
  baseCurrency: ShopCurrency;
  quoteCurrency: ShopCurrency;
  rate: number;
  effectiveFrom: string;
  source: string;
  createdAt: string;
};

export type MerchantShopItem = {
  shopId: string;
  shopName: string;
  branchId: string | null;
  branchName: string | null;
  roleName: string;
  permissions: string[];
  status: ShopStatus;
  verifiedLevel: ShopVerifiedLevel | null;
  /** VAT  POS ເລີ່ມຕົ້ນ (%) */
  defaultTaxPercent: number;
  /** ສະກຸນເລີ່ມຕົ້ນ (ຮ້ານ ຫຼື ສາຂາທັບ) */
  defaultCurrency: ShopCurrency;
  /** ສະກຸນຈາກຕາຕະລາງ shops — ຕັ້ງຄ່າແຜງຄວບຄຸມ */
  shopDefaultCurrency: ShopCurrency;
  enforceSingleShopSkuCurrency: boolean;
  featureFlags: ShopFeatureFlags;
};

export type MerchantContextResponse = {
  shopId: string;
  branchId: string | null;
  roleName: string;
  permissions: string[];
  status: ShopStatus | null;
  verifiedLevel: ShopVerifiedLevel | null;
  defaultCurrency: ShopCurrency | null;
  featureFlags: ShopFeatureFlags | null;
};

// ---------------------------------------------------------------------------
// Types — Shop roles
// ---------------------------------------------------------------------------

export type ShopRole = {
  id: string;
  shopId: string;
  name: string;
  permissions: string[];
  sortOrder: number;
};

// ---------------------------------------------------------------------------
// Types — Shop staff (พนักงานในร้าน)
// ---------------------------------------------------------------------------

export type ShopStaff = {
  id: string;
  shopId: string;
  branchId: string | null;
  branchName: string | null;
  userId: string;
  shopRoleId: string | null;
  roleName: string | null;
  isActive: boolean;
  createdAt: string;
  userName: string | null;
  userEmail: string;
};

// ---------------------------------------------------------------------------
// API — Merchant (shops)
// ---------------------------------------------------------------------------

export const merchantApi = {
  async getShops(): Promise<{ items: MerchantShopItem[] }> {
    return fetcher.get<{ items: MerchantShopItem[] }>(
      `${config.apiUrl}/merchant/shops`,
    );
  },

  async getShopContext(shopId: string): Promise<MerchantContextResponse> {
    return fetcher.get<MerchantContextResponse>(
      `${config.apiUrl}/shops/${shopId}`,
    );
  },

  async patchShopSettings(
    shopId: string,
    body: UpdateShopSettingsDTO,
  ): Promise<{
    defaultCurrency: ShopCurrency;
    defaultTaxPercent: number;
    enforceSingleShopSkuCurrency: boolean;
  }> {
    return fetcher.patch(`${config.apiUrl}/shops/${shopId}/settings`, body);
  },

  async listExchangeRates(
    shopId: string,
    asOf?: string,
  ): Promise<{ asOf: string; items: EffectiveExchangeRateItem[] }> {
    const q =
      asOf !== undefined && asOf !== ""
        ? `?asOf=${encodeURIComponent(asOf)}`
        : "";
    return fetcher.get<{ asOf: string; items: EffectiveExchangeRateItem[] }>(
      `${config.apiUrl}/shops/${shopId}/exchange-rates${q}`,
    );
  },

  async listExchangeRateHistory(
    shopId: string,
    q: Partial<ExchangeRateHistoryQueryDTO>,
  ): Promise<{
    items: ExchangeRateHistoryItem[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(
      `${config.apiUrl}/shops/${shopId}/exchange-rates/history`,
    );
    if (q.baseCurrency) {
      url.searchParams.set("baseCurrency", q.baseCurrency);
    }
    if (q.quoteCurrency) {
      url.searchParams.set("quoteCurrency", q.quoteCurrency);
    }
    if (q.limit != null) {
      url.searchParams.set("limit", String(q.limit));
    }
    if (q.offset != null) {
      url.searchParams.set("offset", String(q.offset));
    }
    return fetcher.get<{
      items: ExchangeRateHistoryItem[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString());
  },

  async createExchangeRate(
    shopId: string,
    body: CreateExchangeRateDTO,
  ): Promise<{
    id: string;
    baseCurrency: ShopCurrency;
    quoteCurrency: ShopCurrency;
    rate: number;
    effectiveFrom: string;
    source: string;
  }> {
    return fetcher.post(
      `${config.apiUrl}/shops/${shopId}/exchange-rates`,
      body,
    );
  },

  async bulkCreateExchangeRates(
    shopId: string,
    body: BulkCreateExchangeRatesDTO,
  ): Promise<{ inserted: number; effectiveFrom: string }> {
    return fetcher.post(
      `${config.apiUrl}/shops/${shopId}/exchange-rates/bulk`,
      body,
    );
  },

  async convertCurrency(
    shopId: string,
    body: ConvertCurrencyDTO,
  ): Promise<{
    amount: number;
    fromCurrency: ShopCurrency;
    toCurrency: ShopCurrency;
    convertedAmount: number;
    multiplier: number;
    asOf: string;
  }> {
    return fetcher.post(
      `${config.apiUrl}/shops/${shopId}/exchange-rates/convert`,
      body,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop roles
// ---------------------------------------------------------------------------

function shopRolesBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/roles`;
}

export const shopRolesApi = {
  list(shopId: string): Promise<{ items: ShopRole[] }> {
    return fetcher.get<{ items: ShopRole[] }>(shopRolesBaseUrl(shopId));
  },

  getAvailablePermissions(shopId: string): Promise<{ items: string[] }> {
    return fetcher.get<{ items: string[] }>(
      `${shopRolesBaseUrl(shopId)}/permissions/available`,
    );
  },

  get(shopId: string, roleId: string): Promise<ShopRole> {
    return fetcher.get<ShopRole>(`${shopRolesBaseUrl(shopId)}/${roleId}`);
  },

  create(
    shopId: string,
    data: { name: string; permissions: string[]; sortOrder?: number },
  ): Promise<ShopRole> {
    return fetcher.post<ShopRole>(shopRolesBaseUrl(shopId), data);
  },

  update(
    shopId: string,
    roleId: string,
    data: { name?: string; permissions?: string[]; sortOrder?: number },
  ): Promise<ShopRole> {
    return fetcher.patch<ShopRole>(
      `${shopRolesBaseUrl(shopId)}/${roleId}`,
      data,
    );
  },

  delete(shopId: string, roleId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${shopRolesBaseUrl(shopId)}/${roleId}`,
    );
  },

  seedDefault(shopId: string): Promise<{ items: ShopRole[] }> {
    return fetcher.post<{ items: ShopRole[] }>(
      `${shopRolesBaseUrl(shopId)}/seed-default`,
      {},
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop staff
// ---------------------------------------------------------------------------

function shopStaffBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/staff`;
}

export type ShopStaffListResult = {
  data: ShopStaff[];
  meta: { total: number; limit: number; offset: number };
};

export const shopStaffApi = {
  list(
    shopId: string,
    query: Partial<ShopStaffListQueryDTO> = {},
  ): Promise<ShopStaffListResult> {
    const url = new URL(shopStaffBaseUrl(shopId));
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    if (query.expandAssignments === true) {
      url.searchParams.set("expandAssignments", "true");
    }
    return fetcher.get<ShopStaffListResult>(url.toString());
  },

  get(shopId: string, staffId: string): Promise<ShopStaff> {
    return fetcher.get<ShopStaff>(`${shopStaffBaseUrl(shopId)}/${staffId}`);
  },

  create(
    shopId: string,
    data: {
      userId: string;
      shopRoleId?: string | null;
      branchId?: string | null;
    },
  ): Promise<ShopStaff> {
    return fetcher.post<ShopStaff>(shopStaffBaseUrl(shopId), data);
  },

  /** สร้าง user + staff (บันทึกลง user/account และ shop_staff) */
  createWithAccount(
    shopId: string,
    data: {
      email: string;
      name: string;
      password?: string;
      shopRoleId?: string | null;
      branchId?: string | null;
    },
  ): Promise<ShopStaff> {
    return fetcher.post<ShopStaff>(
      `${shopStaffBaseUrl(shopId)}/with-account`,
      data,
    );
  },

  /** โหลดข้อมูล user ของ staff สำหรับฟอร์มแก้ไข */
  getUserDetails(
    shopId: string,
    staffId: string,
  ): Promise<{
    id: string;
    email: string;
    name: string;
    image: string | null;
    roleIds: string[];
  }> {
    return fetcher.get(`${shopStaffBaseUrl(shopId)}/${staffId}/user-details`);
  },

  /** แก้ไขทั้ง user และ staff */
  updateWithAccount(
    shopId: string,
    staffId: string,
    data: {
      email?: string;
      name?: string;
      password?: string;
      shopRoleId?: string | null;
      branchId?: string | null;
      isActive?: boolean;
    },
  ): Promise<ShopStaff> {
    return fetcher.patch<ShopStaff>(
      `${shopStaffBaseUrl(shopId)}/${staffId}/with-account`,
      data,
    );
  },

  update(
    shopId: string,
    staffId: string,
    data: {
      shopRoleId?: string | null;
      branchId?: string | null;
      isActive?: boolean;
    },
  ): Promise<ShopStaff> {
    return fetcher.patch<ShopStaff>(
      `${shopStaffBaseUrl(shopId)}/${staffId}`,
      data,
    );
  },

  delete(shopId: string, staffId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${shopStaffBaseUrl(shopId)}/${staffId}`,
    );
  },

  /** ລົບມອບໝາຍພະນັກງານທັງໝົດໃນຮ້ານສຳລັບ user ນີ້ */
  deleteAllForUser(
    shopId: string,
    userId: string,
  ): Promise<{ ok: boolean; removed: number }> {
    return fetcher.delete<{ ok: boolean; removed: number }>(
      `${shopStaffBaseUrl(shopId)}/by-user/${encodeURIComponent(userId)}`,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop branches (ຈັດການສາຂາ)
// ---------------------------------------------------------------------------

function shopBranchesBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/branches`;
}

export type ShopBranchAdmin = {
  id: string;
  shopId: string;
  name: string;
  phone: string | null;
  address: string | null;
  isActive: boolean;
  /** null = ສືບທອດຈາກຮ້ານ */
  defaultCurrency: ShopCurrency | null;
  createdAt: string;
};

export const shopBranchesApi = {
  list(shopId: string): Promise<{ items: ShopBranchAdmin[] }> {
    return fetcher.get(`${shopBranchesBaseUrl(shopId)}`);
  },

  get(shopId: string, branchId: string): Promise<ShopBranchAdmin> {
    return fetcher.get(`${shopBranchesBaseUrl(shopId)}/${branchId}`);
  },

  create(shopId: string, data: CreateShopBranchDTO): Promise<ShopBranchAdmin> {
    return fetcher.post(`${shopBranchesBaseUrl(shopId)}`, data);
  },

  update(
    shopId: string,
    branchId: string,
    data: UpdateShopBranchDTO,
  ): Promise<ShopBranchAdmin> {
    return fetcher.patch(`${shopBranchesBaseUrl(shopId)}/${branchId}`, data);
  },
};

// ---------------------------------------------------------------------------
// API — Shift assignments (ມອບໝາຍກະ / S1)
// ---------------------------------------------------------------------------

function shopShiftAssignmentsBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/shifts/assignments`;
}

export type ShopShiftAssignmentRow = {
  id: string;
  shopId: string;
  branchId: string;
  branchName: string;
  shopStaffId: string;
  workDate: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  status: "scheduled" | "confirmed" | "canceled";
  clockInAt: string | null;
  clockOutAt: string | null;
  note: string | null;
  staffUserName: string | null;
  staffUserEmail: string;
};

export type ShopShiftReportByDayRow = {
  workDate: string;
  assignmentCount: number;
  withClockInCount: number;
  fullyClockedCount: number;
};

export type ShopShiftReportByStaffRow = {
  shopStaffId: string;
  staffUserName: string | null;
  staffUserEmail: string;
  assignmentCount: number;
  withClockInCount: number;
  fullyClockedCount: number;
};

export type ShopShiftReportSummary = {
  byDay: ShopShiftReportByDayRow[];
  byStaff: ShopShiftReportByStaffRow[];
};

export type ShopStaffCalendarOption = {
  id: string;
  userName: string | null;
  userEmail: string;
};

export const shopShiftAssignmentsApi = {
  list(
    shopId: string,
    query: ShiftAssignmentsListQueryDTO,
  ): Promise<{ items: ShopShiftAssignmentRow[] }> {
    const url = new URL(shopShiftAssignmentsBaseUrl(shopId));
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("from", query.from);
    url.searchParams.set("to", query.to);
    if (query.shopStaffId) {
      url.searchParams.set("shopStaffId", query.shopStaffId);
    }
    return fetcher.get(url.toString());
  },

  staffOptions(
    shopId: string,
    query: ShiftStaffOptionsQueryDTO,
  ): Promise<{ items: ShopStaffCalendarOption[] }> {
    const url = new URL(`${shopShiftAssignmentsBaseUrl(shopId)}/staff-options`);
    url.searchParams.set("branchId", query.branchId);
    if (query.forPosClock === "1") {
      url.searchParams.set("forPosClock", "1");
    }
    return fetcher.get(url.toString());
  },

  create(
    shopId: string,
    data: CreateShopShiftAssignmentDTO,
  ): Promise<ShopShiftAssignmentRow> {
    return fetcher.post(shopShiftAssignmentsBaseUrl(shopId), data);
  },

  update(
    shopId: string,
    assignmentId: string,
    data: UpdateShopShiftAssignmentDTO,
  ): Promise<ShopShiftAssignmentRow> {
    return fetcher.patch(
      `${shopShiftAssignmentsBaseUrl(shopId)}/${encodeURIComponent(assignmentId)}`,
      data,
    );
  },

  clockIn(
    shopId: string,
    assignmentId: string,
  ): Promise<ShopShiftAssignmentRow> {
    return fetcher.post(
      `${shopShiftAssignmentsBaseUrl(shopId)}/${encodeURIComponent(assignmentId)}/clock-in`,
    );
  },

  open(
    shopId: string,
    data: { branchId: string; shopStaffId: string; workDate?: string },
  ): Promise<{ assignment: ShopShiftAssignmentRow; created: boolean }> {
    return fetcher.post(`${shopShiftAssignmentsBaseUrl(shopId)}/open`, data);
  },

  clockOut(
    shopId: string,
    assignmentId: string,
  ): Promise<ShopShiftAssignmentRow> {
    return fetcher.post(
      `${shopShiftAssignmentsBaseUrl(shopId)}/${encodeURIComponent(assignmentId)}/clock-out`,
    );
  },

  reportSummary(
    shopId: string,
    query: ShiftAssignmentsListQueryDTO,
  ): Promise<ShopShiftReportSummary> {
    const url = new URL(
      `${shopShiftAssignmentsBaseUrl(shopId)}/report/summary`,
    );
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("from", query.from);
    url.searchParams.set("to", query.to);
    return fetcher.get(url.toString());
  },
};

// ---------------------------------------------------------------------------
// API — Shift swap requests (S3)
// ---------------------------------------------------------------------------

function shopShiftSwapRequestsBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/shifts/swap-requests`;
}

export type ShopShiftSwapRequestRow = {
  id: string;
  shopId: string;
  branchId: string;
  branchName: string;
  status: "pending" | "approved" | "rejected" | "canceled";
  note: string | null;
  requestedAt: string;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  workDate: string;
  sourceAssignmentId: string;
  targetAssignmentId: string;
  requesterShopStaffId: string;
  sourceShopStaffId: string;
  targetShopStaffId: string;
  requesterUserName: string | null;
  requesterUserEmail: string;
  sourceStaffUserEmail: string;
  targetStaffUserEmail: string;
};

export const shopShiftSwapRequestsApi = {
  list(
    shopId: string,
    query: ShopShiftSwapRequestsListQueryDTO,
  ): Promise<{ items: ShopShiftSwapRequestRow[] }> {
    const url = new URL(shopShiftSwapRequestsBaseUrl(shopId));
    url.searchParams.set("branchId", query.branchId);
    if (query.status) {
      url.searchParams.set("status", query.status);
    }
    return fetcher.get(url.toString());
  },

  create(
    shopId: string,
    data: CreateShopShiftSwapRequestDTO,
  ): Promise<ShopShiftSwapRequestRow> {
    return fetcher.post(shopShiftSwapRequestsBaseUrl(shopId), data);
  },

  approve(shopId: string, requestId: string): Promise<ShopShiftSwapRequestRow> {
    return fetcher.post(
      `${shopShiftSwapRequestsBaseUrl(shopId)}/${encodeURIComponent(requestId)}/approve`,
    );
  },

  reject(shopId: string, requestId: string): Promise<ShopShiftSwapRequestRow> {
    return fetcher.post(
      `${shopShiftSwapRequestsBaseUrl(shopId)}/${encodeURIComponent(requestId)}/reject`,
    );
  },

  cancel(shopId: string, requestId: string): Promise<ShopShiftSwapRequestRow> {
    return fetcher.post(
      `${shopShiftSwapRequestsBaseUrl(shopId)}/${encodeURIComponent(requestId)}/cancel`,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop catalog (สินค้าขายได้ / POS)
// ---------------------------------------------------------------------------

function shopCatalogBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/catalog`;
}

function shopServicesBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/services`;
}

export type ShopServiceRow = {
  id: string;
  name: string;
  price: number;
  currency: string;
  isActive: boolean;
  status: string;
};

export const shopServicesApi = {
  list(
    shopId: string,
    query: { q?: string; limit?: number; offset?: number },
  ): Promise<{ data: ShopServiceRow[]; meta: { total: number } }> {
    const url = new URL(shopServicesBaseUrl(shopId));
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q) url.searchParams.set("q", query.q);
    return fetcher.get<{ data: ShopServiceRow[]; meta: { total: number } }>(
      url.toString(),
    );
  },
  create(
    shopId: string,
    body: { name: string; price: number; currency?: string },
  ): Promise<{ ok: boolean; shopProductSkuId: string }> {
    return fetcher.post<{ ok: boolean; shopProductSkuId: string }>(
      shopServicesBaseUrl(shopId),
      body,
    );
  },
  update(
    shopId: string,
    shopProductSkuId: string,
    body: {
      name?: string;
      price?: number;
      currency?: string;
      isActive?: boolean;
    },
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${shopServicesBaseUrl(shopId)}/${shopProductSkuId}`,
      body,
    );
  },
  remove(shopId: string, shopProductSkuId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${shopServicesBaseUrl(shopId)}/${shopProductSkuId}`,
    );
  },
};

export type ShopSellableCatalogRow = {
  id: string;
  catalogProductId: string;
  catalogSkuId: string;
  imageUrl: string | null;
  productName: string;
  productType: string;
  /** master status — "candidate" = ລໍ Admin ອະນຸມັດ (ສະແດງໃນລາຍການ ແຕ່ຍັງຂາຍບໍ່ໄດ້) */
  status: "draft" | "active" | "hidden" | "archived" | "candidate";
  categoryId: string | null;
  categoryName: string | null;
  brandId: string | null;
  brandName: string | null;
  shopSku: string;
  barcode: string | null;
  /** false = ຮ້ານປິດ SKU (ຍັງເຫັນໃນລາຍການ ແຕ່ຂາຍທີ່ POS ບໍ່ໄດ້) */
  isActive: boolean;
  price: number;
  currency: string;
  cost: number | null;
  /** none | lot | lot_serial — ຮັບເຂົ້າ/ຂາຍຕາມ L0+ */
  trackingMode: string;
  /** ຄ່າເລີ່ມຕົ້ນປະກັນ — 0 = ບໍ່ມີ; null = ຍັງບໍ່ຕັ້ງ */
  warrantyDurationMonths: number | null;
  warrantyMaxOdometerKm: number | null;
  warrantyTermsNote: string | null;
  oemPartNo: string | null;
  condition: string | null;
  /** pending = ລໍ Admin ຄີ fitment (ຍັງຂຶ້ນ marketplace ບໍ່ໄດ້); complete = ພ້ອມ */
  fitmentStatus: "pending" | "complete";
  /** ມີແຖວ inventories (sku_id) = ຕົວເລກ; ບໍ່ມີ = ຍັງບໍ່ຕິດຕາມສະຕ໋ອກ */
  quantityOnHand: number | null;
};

export type ShopSellableCatalogListResult = {
  data: ShopSellableCatalogRow[];
  meta: { total: number; limit: number; offset: number };
};

/** รายละเอียด sellable SKU เดียว — เพิ่ม marketplace eligibility */
export type ShopSellableCatalogDetail = ShopSellableCatalogRow & {
  marketplaceEligible: boolean;
  eligibilityReasons: string[];
  availableQty: number;
};

/** รูป SKU (media_assets owner_type=catalog_sku, tag=image) */
export type ShopSkuMediaItem = {
  id: string;
  url: string;
  tag: string | null;
  sortOrder: number | null;
  createdAt: string;
};

export type ShopSellableCategoryOption = {
  id: string;
  name: string;
  parentId?: string | null;
  /** Number of sellable products directly in this category (list endpoint only). */
  count?: number;
};

export type ShopMasterCategoryOption = {
  id: string;
  name: string;
  parentId: string | null;
};

export type PosProvisionSellableResult = {
  ok: true;
  catalogProductId: string;
  catalogSkuId: string;
  shopProductSkuId: string;
  linked: boolean;
  candidate: boolean;
};

export type CatalogSuggestMatchRow = {
  catalogProductId: string;
  catalogSkuId: string;
  productName: string;
  oemPartNo: string | null;
  brandName: string | null;
  score: number;
  reasons: Array<"name_similar" | "same_category" | "same_brand">;
};

export type CatalogSuggestResult = {
  items: CatalogSuggestMatchRow[];
};

export type CatalogSkuMatchRow = {
  catalogProductId: string;
  catalogSkuId: string;
  productName: string;
  oemPartNo: string | null;
  matchKind: "oem_exact" | "alternate" | "supersession";
  matchedVia: string;
};

export type CatalogMatchResult = {
  items: CatalogSkuMatchRow[];
};

export type LinkableCatalogSkuRow = {
  catalogSkuId: string;
  oemPartNo: string | null;
  condition: "new" | "used" | null;
  alreadyLinked: boolean;
};

export type LinkableCatalogProductRow = {
  catalogProductId: string;
  productName: string;
  type: string;
  skus: LinkableCatalogSkuRow[];
};

export type LinkableCatalogResult = {
  items: LinkableCatalogProductRow[];
};

export const shopCatalogApi = {
  listSellable(
    shopId: string,
    query: SellableCatalogQueryDTO,
  ): Promise<ShopSellableCatalogListResult> {
    const url = new URL(`${shopCatalogBaseUrl(shopId)}/sellable`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q) url.searchParams.set("q", query.q);
    if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query.productType)
      url.searchParams.set("productType", query.productType);
    if (query.fitment) url.searchParams.set("fitment", query.fitment);
    if (query.currency) url.searchParams.set("currency", query.currency);
    if (query.barcode) url.searchParams.set("barcode", query.barcode);
    if (query.includePending) url.searchParams.set("includePending", "true");
    if (query.includeInactive) url.searchParams.set("includeInactive", "true");
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ShopSellableCatalogListResult>(url.toString());
  },

  fitmentPendingCount(shopId: string): Promise<{ count: number }> {
    return fetcher.get<{ count: number }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/fitment-pending-count`,
    );
  },

  listSkuMedia(
    shopId: string,
    shopProductSkuId: string,
  ): Promise<ShopSkuMediaItem[]> {
    return fetcher.get<ShopSkuMediaItem[]>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}/media`,
    );
  },

  /**
   * เหมือน listSkuMedia แต่ไม่ยิง toast ตอน error — ใช้ตอน prefetch หลายแถวพร้อมกัน
   * (fetcher.ts toast ทุก error ⇒ 20 แถวที่ล้ม = 20 toasts)
   */
  async listSkuMediaQuiet(
    shopId: string,
    shopProductSkuId: string,
  ): Promise<ShopSkuMediaItem[]> {
    const res = await fetch(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}/media`,
      { credentials: "include" },
    );
    if (!res.ok) throw new Error(`media ${res.status}`);
    return (await res.json()) as ShopSkuMediaItem[];
  },

  addSkuMedia(
    shopId: string,
    shopProductSkuId: string,
    url: string,
  ): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}/media`,
      { url },
    );
  },

  deleteSkuMedia(
    shopId: string,
    shopProductSkuId: string,
    mediaId: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}/media/${mediaId}`,
    );
  },

  reorderSkuMedia(
    shopId: string,
    shopProductSkuId: string,
    mediaIds: string[],
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}/media/reorder`,
      { mediaIds },
    );
  },

  listSellableCategories(
    shopId: string,
  ): Promise<{ items: ShopSellableCategoryOption[] }> {
    return fetcher.get<{ items: ShopSellableCategoryOption[] }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/categories`,
    );
  },

  /** หมวด master ทั้งชุด + parentId — cascading select */
  listMasterCategories(
    shopId: string,
  ): Promise<{ items: ShopMasterCategoryOption[] }> {
    return fetcher.get<{ items: ShopMasterCategoryOption[] }>(
      `${shopCatalogBaseUrl(shopId)}/categories`,
    );
  },

  /** หมวด master catalog — ใช้ตอน provision สินค้าใหม่ */
  lookupMasterCategories(
    shopId: string,
    params: { page: number; search?: string; limit?: number },
  ): Promise<{
    items: ShopSellableCategoryOption[];
    nextPage: number | null;
  }> {
    const url = new URL(`${shopCatalogBaseUrl(shopId)}/categories/lookup`);
    url.searchParams.set("page", String(params.page));
    url.searchParams.set("limit", String(params.limit ?? 20));
    if (params.search) url.searchParams.set("search", params.search);
    return fetcher.get(url.toString());
  },

  getMasterCategory(
    shopId: string,
    categoryId: string,
  ): Promise<ShopMasterCategoryOption | null> {
    return fetcher.get<ShopMasterCategoryOption | null>(
      `${shopCatalogBaseUrl(shopId)}/categories/${categoryId}`,
    );
  },

  /** ค้นหาหมวดที่มีสินค้าขายได้ในร้าน (แบ่งหน้า — InfiniteCombobox) */
  lookupSellableCategories(
    shopId: string,
    params: { page: number; search?: string; limit?: number },
  ): Promise<{
    items: ShopSellableCategoryOption[];
    nextPage: number | null;
  }> {
    const url = new URL(
      `${shopCatalogBaseUrl(shopId)}/sellable/categories/lookup`,
    );
    url.searchParams.set("page", String(params.page));
    url.searchParams.set("limit", String(params.limit ?? 20));
    if (params.search) url.searchParams.set("search", params.search);
    return fetcher.get(url.toString());
  },

  /** หมวดเดียวตาม id (ถ้าไม่มีสินค้าขายได้ในร้านจะได้ null) */
  getSellableCategory(
    shopId: string,
    categoryId: string,
  ): Promise<ShopSellableCategoryOption | null> {
    return fetcher.get<ShopSellableCategoryOption | null>(
      `${shopCatalogBaseUrl(shopId)}/sellable/categories/${categoryId}`,
    );
  },

  getSellableSku(
    shopId: string,
    shopProductSkuId: string,
  ): Promise<ShopSellableCatalogDetail> {
    return fetcher.get<ShopSellableCatalogDetail>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}`,
    );
  },

  patchSellableSku(
    shopId: string,
    shopProductSkuId: string,
    body: UpdateShopProductSkuDTO,
  ): Promise<ShopSellableCatalogRow> {
    return fetcher.patch<ShopSellableCatalogRow>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}`,
      body,
    );
  },

  /** ເປີດ/ປິດ SKU ຂາຍໄດ້ (isActive) */
  setSellableActive(
    shopId: string,
    shopProductSkuId: string,
    isActive: boolean,
  ): Promise<{ ok: boolean; isActive: boolean }> {
    return fetcher.patch<{ ok: boolean; isActive: boolean }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}/active`,
      { isActive },
    );
  },

  /** ລຶບ SKU ອອກຈາກຮ້ານ (409 ຖ້າຖືກໃຊ້/ຂາຍແລ້ວ) */
  deleteSellable(
    shopId: string,
    shopProductSkuId: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${shopCatalogBaseUrl(shopId)}/sellable/${shopProductSkuId}`,
    );
  },

  provisionSellableProduct(
    shopId: string,
    body: ProvisionPosSellableProductDTO,
  ): Promise<PosProvisionSellableResult> {
    return fetcher.post<PosProvisionSellableResult>(
      `${shopCatalogBaseUrl(shopId)}/provision-sellable`,
      body,
    );
  },

  matchCatalog(
    shopId: string,
    query: { oemPartNo?: string; q?: string; barcode?: string; limit?: number },
  ): Promise<CatalogMatchResult> {
    const url = new URL(`${shopCatalogBaseUrl(shopId)}/match`);
    if (query.oemPartNo) url.searchParams.set("oemPartNo", query.oemPartNo);
    if (query.q) url.searchParams.set("q", query.q);
    if (query.barcode) url.searchParams.set("barcode", query.barcode);
    if (query.limit != null) url.searchParams.set("limit", String(query.limit));
    return fetcher.get<CatalogMatchResult>(url.toString());
  },

  suggestCatalog(
    shopId: string,
    query: {
      name: string;
      brandId?: string;
      categoryId?: string;
      type?:
        | "part_new"
        | "part_oem"
        | "part_aftermarket"
        | "part_used"
        | "service";
      limit?: number;
    },
  ): Promise<CatalogSuggestResult> {
    const url = new URL(`${shopCatalogBaseUrl(shopId)}/suggest`);
    url.searchParams.set("name", query.name);
    if (query.brandId) url.searchParams.set("brandId", query.brandId);
    if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query.type) url.searchParams.set("type", query.type);
    if (query.limit != null) url.searchParams.set("limit", String(query.limit));
    return fetcher.get<CatalogSuggestResult>(url.toString());
  },

  listLinkableCatalog(
    shopId: string,
    query: { categoryId: string; q?: string; limit?: number },
  ): Promise<LinkableCatalogResult> {
    const url = new URL(`${shopCatalogBaseUrl(shopId)}/linkable`);
    url.searchParams.set("categoryId", query.categoryId);
    if (query.q) url.searchParams.set("q", query.q);
    if (query.limit != null) url.searchParams.set("limit", String(query.limit));
    return fetcher.get<LinkableCatalogResult>(url.toString());
  },
};

// ---------------------------------------------------------------------------
// API — Product issue claims (Claim C0)
// ---------------------------------------------------------------------------

function shopProductClaimsBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/product-claims`;
}

export type ShopProductClaimListItem = {
  id: string;
  branchId: string;
  branchName: string;
  status: string;
  issueType: string;
  title: string | null;
  posSaleId: string | null;
  supplierId: string | null;
  supplierName: string | null;
  createdByUserId: string;
  submittedAt: string | null;
  /** W2 — ຜູກສິດປະກັນຕາມບຣັດທາງ */
  saleLineWarrantyId: string | null;
  /** W2 — snapshot ຫຼັງສົ່ງ */
  warrantyEligibilityAtSubmit: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ShopProductClaimLineDetail = {
  id: string;
  shopProductSkuId: string;
  shopSku: string;
  posSaleItemId: string | null;
  qty: number;
  inventoryLotId: string | null;
  inventorySerialId: string | null;
  lineNote: string | null;
};

export type ShopProductClaimAttachmentItem = {
  id: string;
  storageKey: string;
  contentType: string | null;
  fileName: string | null;
  createdAt: string;
};

export type ShopProductClaimDetail = {
  id: string;
  shopId: string;
  branchId: string;
  branchName: string;
  status: string;
  issueType: string;
  title: string | null;
  description: string | null;
  posSaleId: string | null;
  supplierId: string | null;
  supplierName: string | null;
  stockImpactAppliedAt: string | null;
  createdByUserId: string;
  submittedAt: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolutionNote: string | null;
  saleLineWarrantyId: string | null;
  warrantyEligibilityAtSubmit: string | null;
  /** W3 — ເລກໄມລ໌ຕອນເປີດເຄສ */
  odometerAtClaimKm: number | null;
  /** W3 — ລູກຄ້າ/ຍານພາຫະນະ */
  vehicleNote: string | null;
  createdAt: string;
  updatedAt: string;
  lines: ShopProductClaimLineDetail[];
  attachments: ShopProductClaimAttachmentItem[];
};

export type ShopProductClaimListResult = {
  items: ShopProductClaimListItem[];
  meta: { total: number; limit: number; offset: number };
};

export const shopProductClaimsApi = {
  list(
    shopId: string,
    query: ShopProductClaimListQueryDTO,
  ): Promise<ShopProductClaimListResult> {
    const url = new URL(shopProductClaimsBaseUrl(shopId));
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.supplierId) url.searchParams.set("supplierId", query.supplierId);
    if (query.warrantyLink && query.warrantyLink !== "any") {
      url.searchParams.set("warrantyLink", query.warrantyLink);
    }
    return fetcher.get<ShopProductClaimListResult>(url.toString());
  },

  get(shopId: string, claimId: string): Promise<ShopProductClaimDetail> {
    return fetcher.get<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}`,
    );
  },

  create(
    shopId: string,
    body: CreateShopProductClaimDTO,
  ): Promise<ShopProductClaimDetail> {
    return fetcher.post<ShopProductClaimDetail>(
      shopProductClaimsBaseUrl(shopId),
      body,
    );
  },

  updateDraft(
    shopId: string,
    claimId: string,
    body: UpdateShopProductClaimDraftDTO,
  ): Promise<ShopProductClaimDetail> {
    return fetcher.patch<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}`,
      body,
    );
  },

  submit(shopId: string, claimId: string): Promise<ShopProductClaimDetail> {
    return fetcher.post<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}/submit`,
    );
  },

  cancel(shopId: string, claimId: string): Promise<ShopProductClaimDetail> {
    return fetcher.post<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}/cancel`,
    );
  },

  setStatus(
    shopId: string,
    claimId: string,
    body: SetShopProductClaimStatusDTO,
  ): Promise<ShopProductClaimDetail> {
    return fetcher.patch<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}/status`,
      body,
    );
  },

  addAttachment(
    shopId: string,
    claimId: string,
    body: AddShopProductClaimAttachmentDTO,
  ): Promise<ShopProductClaimDetail> {
    return fetcher.post<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}/attachments`,
      body,
    );
  },

  removeAttachment(
    shopId: string,
    claimId: string,
    attachmentId: string,
  ): Promise<ShopProductClaimDetail> {
    return fetcher.delete<ShopProductClaimDetail>(
      `${shopProductClaimsBaseUrl(shopId)}/${claimId}/attachments/${attachmentId}`,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop sale-line warranties (W3)
// ---------------------------------------------------------------------------

function shopWarrantiesBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/warranties`;
}

export type ShopWarrantyExpiringRow = {
  id: string;
  posSaleId: string;
  posSaleItemId: string;
  saleNumber: string;
  shopSku: string;
  soldAt: string;
  coverageEndAt: string;
  coverageEndOdometerKm: number | null;
};

export type ShopWarrantiesExpiringResult = {
  meta: {
    total: number;
    limit: number;
    offset: number;
    withinDays: number;
  };
  items: ShopWarrantyExpiringRow[];
};

export const shopWarrantiesApi = {
  listExpiringSoon(
    shopId: string,
    query: ShopWarrantiesExpiringQueryDTO,
  ): Promise<ShopWarrantiesExpiringResult> {
    const url = new URL(`${shopWarrantiesBaseUrl(shopId)}/expiring-soon`);
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("withinDays", String(query.withinDays ?? 30));
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get<ShopWarrantiesExpiringResult>(url.toString());
  },
};

// ---------------------------------------------------------------------------
// API — Purchase orders (P0)
// ---------------------------------------------------------------------------

function shopPurchaseOrdersBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/purchase-orders`;
}

export type PurchaseOrderListRow = {
  id: string;
  branchId: string;
  branchName: string | null;
  poNumber: string;
  status: string;
  currency: ShopCurrency;
  orderedAt: string | null;
  createdAt: string;
  lineCount: number;
  totalAmount: number;
  supplierDisplayName: string | null;
};

export type PurchaseOrderLineDetail = {
  id: string;
  shopProductSkuId: string;
  productName: string;
  shopSku: string;
  trackingMode: string;
  qtyOrdered: number;
  qtyReceived: number;
  unitCost: number | null;
  lineNote: string | null;
};

export type PurchaseOrderDetailResponse = {
  id: string;
  shopId: string;
  branchId: string;
  branchName: string | null;
  poNumber: string;
  status: string;
  currency: ShopCurrency;
  supplierId: string | null;
  supplierName: string | null;
  supplierRef: string | null;
  orderedAt: string | null;
  expectedAt: string | null;
  note: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  lines: PurchaseOrderLineDetail[];
};

/** P2: ຫົວໃບຮັບສິນຄ້າ (GRN) ຕໍ່ PO — ສອດຄ່ອງ API GET .../receipts */
export type PurchaseOrderGoodsReceiptListItem = {
  id: string;
  receivedAt: string;
  note: string | null;
  createdByUserId: string | null;
  lines: {
    id: string;
    shopProductSkuId: string;
    productName: string;
    shopSku: string;
    qtyReceived: number;
    warehouseLocationId: string | null;
    locationCode: string | null;
  }[];
};

export const shopPurchaseOrdersApi = {
  list(
    shopId: string,
    query: PurchaseOrderListQueryDTO,
  ): Promise<{
    data: PurchaseOrderListRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(shopPurchaseOrdersBaseUrl(shopId));
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.supplierId) url.searchParams.set("supplierId", query.supplierId);
    if (query.due) url.searchParams.set("due", query.due);
    if (query.sortBy) url.searchParams.set("sortBy", query.sortBy);
    if (query.sortOrder) url.searchParams.set("sortOrder", query.sortOrder);
    return fetcher.get(url.toString());
  },

  create(
    shopId: string,
    body: CreatePurchaseOrderDTO,
  ): Promise<{ id: string; poNumber: string }> {
    return fetcher.post(`${shopPurchaseOrdersBaseUrl(shopId)}`, body);
  },

  get(
    shopId: string,
    purchaseOrderId: string,
  ): Promise<PurchaseOrderDetailResponse> {
    return fetcher.get<PurchaseOrderDetailResponse>(
      `${shopPurchaseOrdersBaseUrl(shopId)}/${purchaseOrderId}`,
    );
  },

  update(
    shopId: string,
    purchaseOrderId: string,
    body: UpdatePurchaseOrderDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch(
      `${shopPurchaseOrdersBaseUrl(shopId)}/${purchaseOrderId}`,
      body,
    );
  },

  submit(shopId: string, purchaseOrderId: string): Promise<{ ok: boolean }> {
    return fetcher.post(
      `${shopPurchaseOrdersBaseUrl(shopId)}/${purchaseOrderId}/submit`,
      {},
    );
  },

  cancel(shopId: string, purchaseOrderId: string): Promise<{ ok: boolean }> {
    return fetcher.post(
      `${shopPurchaseOrdersBaseUrl(shopId)}/${purchaseOrderId}/cancel`,
      {},
    );
  },

  /** P1: ຮັບຂອງຕາມ PO — ສົ່ງ Idempotency-Key ເພື່ອກັນກົດຊ້ຳ */
  receiveGoods(
    shopId: string,
    purchaseOrderId: string,
    body: PurchaseOrderGoodsReceiptDTO,
    idempotencyKey?: string,
  ): Promise<{ receiptId: string; idempotentReplay?: boolean }> {
    return fetcher.post(
      `${shopPurchaseOrdersBaseUrl(shopId)}/${purchaseOrderId}/receipts`,
      body,
      idempotencyKey
        ? { headers: { "Idempotency-Key": idempotencyKey } }
        : undefined,
    );
  },

  /** P2: ປະຫວັດໃບຮັບ (GRN) ຕໍ່ PO */
  listReceipts(
    shopId: string,
    purchaseOrderId: string,
  ): Promise<{ receipts: PurchaseOrderGoodsReceiptListItem[] }> {
    return fetcher.get(
      `${shopPurchaseOrdersBaseUrl(shopId)}/${purchaseOrderId}/receipts`,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Suppliers (P3)
// ---------------------------------------------------------------------------

function shopSuppliersBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/suppliers`;
}

export type SupplierListRow = {
  id: string;
  name: string;
  phone: string | null;
  refCode: string | null;
  note: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  poCount: number;
};

export const shopSuppliersApi = {
  list(
    shopId: string,
    query: SupplierListQueryDTO,
  ): Promise<{
    data: SupplierListRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(shopSuppliersBaseUrl(shopId));
    url.searchParams.set("limit", String(query.limit ?? 50));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.search) url.searchParams.set("search", query.search);
    if (query.activeFilter != null) {
      url.searchParams.set("activeFilter", query.activeFilter);
    } else {
      url.searchParams.set("activeOnly", String(query.activeOnly ?? true));
    }
    return fetcher.get(url.toString());
  },

  create(shopId: string, body: CreateSupplierDTO): Promise<{ id: string }> {
    return fetcher.post(shopSuppliersBaseUrl(shopId), body);
  },

  update(
    shopId: string,
    supplierId: string,
    body: UpdateSupplierDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch(`${shopSuppliersBaseUrl(shopId)}/${supplierId}`, body);
  },
};

// ---------------------------------------------------------------------------
// API — Shop promotions (P8)
// ---------------------------------------------------------------------------

function shopPromotionsBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/promotions`;
}

export type ShopPromotionListRow = {
  id: string;
  name: string;
  status: "draft" | "active" | "paused";
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
  branchId: string | null;
  billCurrency: string | null;
  ruleType: "line_percent_off" | "line_amount_off";
  percentOff: number | null;
  amountOff: number | null;
  targetSkuIds: string[];
  createdAt: string;
  updatedAt: string;
};

export const shopPromotionsApi = {
  list(
    shopId: string,
    query: ShopPromotionListQueryDTO,
  ): Promise<{
    data: ShopPromotionListRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(shopPromotionsBaseUrl(shopId));
    url.searchParams.set("limit", String(query.limit ?? 50));
    url.searchParams.set("offset", String(query.offset ?? 0));
    url.searchParams.set("status", query.status ?? "all");
    if (query.search != null && query.search.trim() !== "") {
      url.searchParams.set("search", query.search.trim().slice(0, 200));
    }
    return fetcher.get(url.toString());
  },

  get(shopId: string, promotionId: string): Promise<ShopPromotionListRow> {
    return fetcher.get(`${shopPromotionsBaseUrl(shopId)}/${promotionId}`);
  },

  create(
    shopId: string,
    body: CreateShopPromotionDTO,
  ): Promise<{ id: string }> {
    return fetcher.post(shopPromotionsBaseUrl(shopId), body);
  },

  update(
    shopId: string,
    promotionId: string,
    body: UpdateShopPromotionDTO,
  ): Promise<{ ok: boolean }> {
    return fetcher.patch(
      `${shopPromotionsBaseUrl(shopId)}/${promotionId}`,
      body,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Marketplace (Phase A)
// ---------------------------------------------------------------------------

function shopMarketplaceBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/marketplace`;
}

export type ShopMarketplaceListRow = {
  shopProductSkuId: string;
  productName: string;
  shopSku: string;
  price: number;
  currency: string;
  fitmentStatus: "pending" | "complete";
  availableQty: number;
  eligible: boolean;
  ineligibleReasons: string[];
  listingId: string | null;
  listingStatus: "active" | "hidden" | "removed" | null;
  published: boolean;
};

export const shopMarketplaceApi = {
  list(
    shopId: string,
    query: ShopMarketplaceListQueryDTO,
  ): Promise<{
    data: ShopMarketplaceListRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(`${shopMarketplaceBaseUrl(shopId)}/listings`);
    url.searchParams.set("limit", String(query.limit ?? 50));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q?.trim()) url.searchParams.set("q", query.q.trim());
    return fetcher.get(url.toString());
  },

  stats(shopId: string): Promise<{
    published: number;
    eligibleUnpublished: number;
    pendingOrders: number;
  }> {
    return fetcher.get(`${shopMarketplaceBaseUrl(shopId)}/stats`);
  },

  publish(
    shopId: string,
    body: PublishMarketplaceListingDTO,
  ): Promise<{ ok: true; listing: { id: string }; created: boolean }> {
    return fetcher.post(`${shopMarketplaceBaseUrl(shopId)}/listings`, body);
  },

  unpublish(shopId: string, listingId: string): Promise<{ ok: true }> {
    return fetcher.patch(
      `${shopMarketplaceBaseUrl(shopId)}/listings/${listingId}/unpublish`,
      {},
    );
  },

  getSettings(shopId: string): Promise<{
    autoPublish: boolean;
    marketplaceSync: boolean;
  }> {
    return fetcher.get(`${shopMarketplaceBaseUrl(shopId)}/settings`);
  },

  updateSettings(
    shopId: string,
    body: { autoPublish: boolean },
  ): Promise<{
    ok: true;
    featureFlags: Record<string, boolean>;
    bulk?: { published: number; skipped: number; failed: number };
  }> {
    return fetcher.patch(`${shopMarketplaceBaseUrl(shopId)}/settings`, body);
  },

  syncAll(shopId: string): Promise<{
    ok: true;
    published: number;
    skipped: number;
    failed: number;
  }> {
    return fetcher.post(`${shopMarketplaceBaseUrl(shopId)}/sync`, {});
  },

  listOrders(
    shopId: string,
    query?: { status?: string; limit?: number; offset?: number },
  ): Promise<{
    data: Array<{
      id: string;
      status: string;
      total: number;
      currency: string;
      createdAt: string;
      customerUserId: string;
      itemCount: number;
    }>;
  }> {
    const url = new URL(`${shopMarketplaceBaseUrl(shopId)}/orders`);
    if (query?.status) url.searchParams.set("status", query.status);
    if (query?.limit != null)
      url.searchParams.set("limit", String(query.limit));
    if (query?.offset != null)
      url.searchParams.set("offset", String(query.offset));
    return fetcher.get(url.toString());
  },

  updateOrderStatus(
    shopId: string,
    orderId: string,
    status: "confirmed" | "ready_pickup" | "completed" | "canceled",
  ): Promise<{ ok: true }> {
    return fetcher.patch(
      `${shopMarketplaceBaseUrl(shopId)}/orders/${orderId}/status`,
      { status },
    );
  },
};

// ---------------------------------------------------------------------------
// API — POS checkout (Phase F)
// ---------------------------------------------------------------------------

function shopPosBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/pos`;
}

export type PosCheckoutResponse = {
  saleId: string;
  saleNumber: string;
  total: number;
  currency: string;
  createdAt: string;
  idempotentReplay: boolean;
  saleStatus: "paid" | "partial";
  paidInInvoice: number;
  balanceDue: number;
};

export type PosCheckoutPreviewResponse = {
  grossSubtotal: number;
  promotionDiscountTotal: number;
  manualLineDiscountTotal: number;
  lineDiscountTotal: number;
  billDiscountTotal: number;
  netBeforeTax: number;
  taxPercentApplied: number;
  taxTotal: number;
  grandTotal: number;
  currency: string;
  lines: Array<{
    shopProductSkuId: string;
    qty: number;
    unitPrice: number;
    lineGross: number;
    promotionDiscount: number;
    manualDiscountBeforeBill: number;
    lineTotal: number;
    promotionApplications: Array<{
      promotionId: string;
      promotionName: string;
      discountAmount: number;
      ruleType: "line_percent_off" | "line_amount_off";
    }>;
  }>;
};

export type PosSaleAddPaymentsResponse = {
  saleId: string;
  saleNumber: string;
  total: number;
  currency: string;
  paidInInvoice: number;
  balanceDue: number;
  saleStatus: "paid" | "partial";
};

export type PosSaleListItem = {
  id: string;
  saleNumber: string;
  branchId: string;
  status: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  taxPercent: number;
  total: number;
  currency: string;
  createdAt: string;
  holdExpiresAt: string | null;
  /** ຄືນຄົບທຸກລາຍການແລ້ວ — ເຊື່ອງປຸ່ມ ຄືນ/ປ່ຽນ */
  fullyReturned: boolean;
  /** ບິນນີ້ຖືກ ປ່ຽນ — ສະແດງປ້າຍ "ປ່ຽນແລ້ວ" */
  exchanged: boolean;
};

export type PosReturnSummaryItem = { productName: string; qty: number };
export type PosReturnSummary = {
  originalSaleId: string;
  type: "return" | "exchange";
  refundTotal: number;
  currency: string;
  oldItems: PosReturnSummaryItem[];
  newItems: PosReturnSummaryItem[];
};

/** ລາຍງານ §3 ທາງເລືອກ 1 — ສະຫຼຸບຕາມສະກຸນບິນ (ບໍ່ແປງ FX) */
export type PosSalesByCurrencyReportResponse = {
  byCurrency: Record<string, { total: number; saleCount: number }>;
  includedSaleCount: number;
};

export type PosSalesByDayReportResponse = {
  days: Array<{
    date: string;
    byCurrency: Record<string, { total: number; saleCount: number }>;
  }>;
};

export type PosTopSkusReportResponse = {
  items: Array<{
    shopProductSkuId: string;
    productName: string;
    shopSku: string;
    lineSum: number;
    qty: number;
    currency: string;
  }>;
};

export type PosSalesByBranchReportResponse = {
  branches: Array<{
    branchId: string;
    branchName: string | null;
    includedSaleCount: number;
    byCurrency: Record<string, { total: number; saleCount: number }>;
  }>;
};

export type PosSaleInvoiceSnapshotResponse = {
  sellerName: string | null;
  sellerTaxId: string | null;
  sellerAddress: string | null;
  buyerName: string | null;
  buyerTaxId: string | null;
  buyerAddress: string | null;
};

export type PosSaleActivityItem = {
  id: string;
  occurredAt: string;
  action: string;
  actorId: string | null;
  actorName: string | null;
  result: string | null;
  before: unknown;
  after: unknown;
  meta: unknown;
};

export type PosSaleDetailResponse = {
  id: string;
  saleNumber: string;
  branchId: string;
  /** ຊື່ສາຂາ — ຈາກ DB ຕອນໂຫຼດລາຍລະອຽດ (ສຳລັບພິມ) */
  branchName: string | null;
  status: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  /** VAT ຕອນອອກບິນ (%) */
  taxPercent: number;
  total: number;
  currency: string;
  createdAt: string;
  holdExpiresAt: string | null;
  holdPayload: unknown;
  invoice: PosSaleInvoiceSnapshotResponse;
  lines: Array<{
    id: string;
    skuId: string;
    productName: string;
    shopSku: string;
    /** ຄັດຈາກ media_assets (catalog_product) — ຫຼື null */
    imageUrl: string | null;
    qty: number;
    /** ຈຳນວນທີ່ຄືນໄປແລ້ວ (pos_returns completed) — ໃຊ້ຈຳກັດຈຳນວນຄືນ/ປ່ຽນ */
    returnedQty: number;
    unitPrice: number;
    discount: number;
    lineTotal: number;
    lotId: string | null;
    serialId: string | null;
    /** lot_no ຈາກ inventory_lots — ສະແດງໃນ UI */
    lotNo: string | null;
    /** serial_no ຈາກ inventory_serials — ສະແດງໃນ UI */
    serialNo: string | null;
    /** ປະກັນຕາມບຣັດທາງ (W1) */
    warranty: {
      id: string;
      status: string;
      soldAt: string;
      coverageStartAt: string;
      coverageEndAt: string;
      coverageEndOdometerKm: number | null;
      termsNoteSnapshot: string | null;
    } | null;
  }>;
  payments: Array<{
    id: string;
    method: string;
    amount: number;
    /** ບິນເກົ່າອາດບໍ່ມີ — ເທົ່າກັບສະກຸນບິນ */
    currency?: string;
    invoiceEquivalentAmount?: number;
    reference: string | null;
    status: string;
    createdAt: string;
  }>;
};

export type PosCreateHoldResponse = {
  saleId: string;
  saleNumber: string;
  total: number;
  currency: string;
  expiresAt: string;
};

export type PosReturnRefundMethod =
  | "cash"
  | "transfer"
  | "wallet"
  | "store_credit";

export type CreatePosReturnBody = {
  originalSaleId: string;
  type: "return" | "exchange";
  reason?: string;
  refundMethod?: PosReturnRefundMethod;
  exchangeSaleId?: string;
  lines: Array<{ saleItemId: string; qty: number; restock: boolean }>;
};

export type PosReturnCreatedResponse = {
  ok: boolean;
  returnId: string;
  returnNumber: string;
  refundTotal: number;
  currency: string;
};

export type PosReturnListRow = {
  id: string;
  returnNumber: string;
  type: "return" | "exchange";
  status: "completed" | "voided";
  originalSaleId: string;
  originalSaleNumber: string;
  exchangeSaleId: string | null;
  refundMethod: string | null;
  refundTotal: number;
  currency: string;
  itemCount: number;
  createdAt: string;
};

export type PosReturnListResponse = {
  data: PosReturnListRow[];
  meta: { total: number };
};

export type PosLotItem = {
  id: string;
  lotNo: string;
  qty: number;
  expiryDate: string | null;
  manufacturedDate: string | null;
  /** ລະຫັດສະແກນປ້າຍ lot (ຖ້າມີ) — ຈາກ inventory_lots.lot_scan_code */
  lotScanCode?: string | null;
};

export type PosSerialItem = {
  id: string;
  serialNo: string;
  lotId?: string | null;
  lotNo?: string | null;
  unitScanCode?: string | null;
};

export type PosSerialLookupResponse = {
  serialId: string;
  serialNo: string;
  shopProductSkuId: string;
  scanMatch?: "unit_scan" | "serial_no";
  lotId?: string | null;
  lotNo?: string | null;
  lotExpiryDate?: string | null;
  lotManufacturedDate?: string | null;
};

/** GET /pos/scan-resolve — ລຳດັບ unit_scan → serial_no → lot_scan → sku barcode */
export type PosScanResolveResult =
  | {
      kind: "unit";
      serialId: string;
      serialNo: string;
      shopProductSkuId: string;
      lotId: string | null;
      lotNo: string | null;
      lotExpiryDate: string | null;
      lotManufacturedDate: string | null;
    }
  | {
      kind: "serial_no";
      serialId: string;
      serialNo: string;
      shopProductSkuId: string;
      lotId: string | null;
      lotNo: string | null;
      lotExpiryDate: string | null;
      lotManufacturedDate: string | null;
    }
  | {
      kind: "lot";
      lotId: string;
      shopProductSkuId: string;
      lotNo: string;
      lotQty: number;
      lotExpiryDate: string | null;
      lotManufacturedDate: string | null;
    }
  | { kind: "sku_barcode"; catalogRow: ShopSellableCatalogRow }
  | { kind: "not_found" };

export type ShopPosCategoriesResult = {
  items: {
    id: string;
    name: string;
    parentId: string | null;
    count: number;
  }[];
  topCategoryIds: string[];
};

export const shopPosApi = {
  listCategories(
    shopId: string,
    params?: { branchId?: string; topDays?: number },
  ): Promise<ShopPosCategoriesResult> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/categories`);
    if (params?.branchId) url.searchParams.set("branchId", params.branchId);
    if (params?.topDays != null) {
      url.searchParams.set("topDays", String(params.topDays));
    }
    return fetcher.get<ShopPosCategoriesResult>(url.toString());
  },

  listSellable(
    shopId: string,
    query: SellableCatalogQueryDTO,
  ): Promise<ShopSellableCatalogListResult> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/sellable`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q) url.searchParams.set("q", query.q);
    if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.barcode) url.searchParams.set("barcode", query.barcode);
    return fetcher.get<ShopSellableCatalogListResult>(url.toString());
  },

  scanResolve(
    shopId: string,
    branchId: string,
    code: string,
  ): Promise<PosScanResolveResult> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/scan-resolve`);
    url.searchParams.set("branchId", branchId);
    url.searchParams.set("code", code);
    return fetcher.get<PosScanResolveResult>(url.toString());
  },

  listSerials(
    shopId: string,
    branchId: string,
    shopProductSkuId: string,
  ): Promise<{ items: PosSerialItem[] }> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/serials`);
    url.searchParams.set("branchId", branchId);
    url.searchParams.set("shopProductSkuId", shopProductSkuId);
    return fetcher.get<{ items: PosSerialItem[] }>(url.toString());
  },

  lookupSerial(
    shopId: string,
    branchId: string,
    code: string,
  ): Promise<PosSerialLookupResponse> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/serials/lookup`);
    url.searchParams.set("branchId", branchId);
    url.searchParams.set("code", code);
    return fetcher.get<PosSerialLookupResponse>(url.toString());
  },

  listLots(
    shopId: string,
    branchId: string,
    shopProductSkuId: string,
  ): Promise<{ items: PosLotItem[] }> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/lots`);
    url.searchParams.set("branchId", branchId);
    url.searchParams.set("shopProductSkuId", shopProductSkuId);
    return fetcher.get<{ items: PosLotItem[] }>(url.toString());
  },

  checkout(
    shopId: string,
    branchId: string,
    body: PosCheckoutDTO,
    idempotencyKey: string,
  ): Promise<PosCheckoutResponse> {
    return fetcher.post<PosCheckoutResponse>(
      `${shopPosBaseUrl(shopId)}/checkout`,
      body,
      {
        headers: {
          "X-Branch-Id": branchId,
          "Idempotency-Key": idempotencyKey,
        },
      },
    );
  },

  checkoutPreview(
    shopId: string,
    branchId: string,
    body: PosCheckoutPreviewDTO,
  ): Promise<PosCheckoutPreviewResponse> {
    return fetcher.post<PosCheckoutPreviewResponse>(
      `${shopPosBaseUrl(shopId)}/checkout/preview`,
      body,
      {
        headers: {
          "X-Branch-Id": branchId,
        },
      },
    );
  },

  listSales(
    shopId: string,
    query: Partial<PosSalesListQueryDTO>,
  ): Promise<{
    items: PosSaleListItem[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/sales`);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.status) url.searchParams.set("status", query.status);
    if (query.returnType) url.searchParams.set("returnType", query.returnType);
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    if (query.saleNumber) url.searchParams.set("saleNumber", query.saleNumber);
    if (query.customerUserId)
      url.searchParams.set("customerUserId", query.customerUserId);
    if (query.customerSearch)
      url.searchParams.set("customerSearch", query.customerSearch);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get(url.toString());
  },

  returnSummaries(
    shopId: string,
    returnType: "return" | "exchange",
    saleIds: string[],
  ): Promise<{ summaries: Record<string, PosReturnSummary> }> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/sales/return-summaries`);
    url.searchParams.set("returnType", returnType);
    url.searchParams.set("saleIds", saleIds.join(","));
    return fetcher.get(url.toString());
  },

  salesByCurrencyReport(
    shopId: string,
    query: Partial<PosSalesByCurrencyReportQueryDTO>,
  ): Promise<PosSalesByCurrencyReportResponse> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/reports/sales-by-currency`);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.branchIds?.length) {
      url.searchParams.set("branchIds", query.branchIds.join(","));
    }
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    return fetcher.get<PosSalesByCurrencyReportResponse>(url.toString());
  },

  salesByDayReport(
    shopId: string,
    query: Partial<PosSalesExtendedReportQueryDTO>,
  ): Promise<PosSalesByDayReportResponse> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/reports/sales-by-day`);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.branchIds?.length) {
      url.searchParams.set("branchIds", query.branchIds.join(","));
    }
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    if (query.limit != null) url.searchParams.set("limit", String(query.limit));
    return fetcher.get<PosSalesByDayReportResponse>(url.toString());
  },

  topSkusReport(
    shopId: string,
    query: Partial<PosSalesExtendedReportQueryDTO>,
  ): Promise<PosTopSkusReportResponse> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/reports/top-skus`);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.branchIds?.length) {
      url.searchParams.set("branchIds", query.branchIds.join(","));
    }
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    url.searchParams.set("limit", String(query.limit ?? 20));
    return fetcher.get<PosTopSkusReportResponse>(url.toString());
  },

  salesByBranchReport(
    shopId: string,
    query: Partial<PosSalesExtendedReportQueryDTO>,
  ): Promise<PosSalesByBranchReportResponse> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/reports/sales-by-branch`);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.branchIds?.length) {
      url.searchParams.set("branchIds", query.branchIds.join(","));
    }
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    if (query.limit != null) url.searchParams.set("limit", String(query.limit));
    return fetcher.get<PosSalesByBranchReportResponse>(url.toString());
  },

  getSale(shopId: string, saleId: string): Promise<PosSaleDetailResponse> {
    return fetcher.get<PosSaleDetailResponse>(
      `${shopPosBaseUrl(shopId)}/sales/${saleId}`,
    );
  },

  getSaleActivity(
    shopId: string,
    saleId: string,
  ): Promise<{ items: PosSaleActivityItem[] }> {
    return fetcher.get<{ items: PosSaleActivityItem[] }>(
      `${shopPosBaseUrl(shopId)}/sales/${saleId}/activity`,
    );
  },

  addSalePayments(
    shopId: string,
    branchId: string,
    saleId: string,
    body: PosSaleAddPaymentsDTO,
  ): Promise<PosSaleAddPaymentsResponse> {
    return fetcher.post<PosSaleAddPaymentsResponse>(
      `${shopPosBaseUrl(shopId)}/sales/${saleId}/payments`,
      body,
      { headers: { "X-Branch-Id": branchId } },
    );
  },

  voidSale(
    shopId: string,
    saleId: string,
    branchId?: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.post<{ ok: boolean }>(
      `${shopPosBaseUrl(shopId)}/sales/${saleId}/void`,
      {},
      {
        headers:
          branchId != null && branchId !== ""
            ? { "X-Branch-Id": branchId }
            : undefined,
      },
    );
  },

  refundSale(
    shopId: string,
    saleId: string,
    branchId?: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.post<{ ok: boolean }>(
      `${shopPosBaseUrl(shopId)}/sales/${saleId}/refund`,
      {},
      {
        headers:
          branchId != null && branchId !== ""
            ? { "X-Branch-Id": branchId }
            : undefined,
      },
    );
  },

  createReturn(
    shopId: string,
    branchId: string,
    body: CreatePosReturnBody,
  ): Promise<PosReturnCreatedResponse> {
    return fetcher.post<PosReturnCreatedResponse>(
      `${shopPosBaseUrl(shopId)}/returns`,
      body,
      { headers: { "X-Branch-Id": branchId } },
    );
  },

  listReturns(
    shopId: string,
    branchId: string,
    query: { limit?: number; offset?: number },
  ): Promise<PosReturnListResponse> {
    const url = new URL(`${shopPosBaseUrl(shopId)}/returns`);
    if (query.limit != null) url.searchParams.set("limit", String(query.limit));
    if (query.offset != null)
      url.searchParams.set("offset", String(query.offset));
    return fetcher.get<PosReturnListResponse>(url.toString(), {
      headers: { "X-Branch-Id": branchId },
    });
  },

  createHold(
    shopId: string,
    branchId: string,
    body: PosCreateHoldDTO,
  ): Promise<PosCreateHoldResponse> {
    return fetcher.post<PosCreateHoldResponse>(
      `${shopPosBaseUrl(shopId)}/holds`,
      body,
      { headers: { "X-Branch-Id": branchId } },
    );
  },

  confirmHold(
    shopId: string,
    branchId: string,
    saleId: string,
    body: PosHoldConfirmDTO,
    idempotencyKey: string,
  ): Promise<PosCheckoutResponse> {
    return fetcher.post<PosCheckoutResponse>(
      `${shopPosBaseUrl(shopId)}/holds/${saleId}/confirm`,
      body,
      {
        headers: {
          "X-Branch-Id": branchId,
          "Idempotency-Key": idempotencyKey,
        },
      },
    );
  },

  abandonHold(
    shopId: string,
    _branchId: string,
    saleId: string,
  ): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${shopPosBaseUrl(shopId)}/holds/${saleId}`,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop reports (P6+ module stats)
// ---------------------------------------------------------------------------

function shopReportsBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/reports`;
}

export type ShopModuleReportStatsResponse =
  import("@/modules/merchant/domain/contracts/shop-module-report-stats").ShopModuleReportStatsDTO;

export const shopReportsApi = {
  moduleStats(shopId: string): Promise<ShopModuleReportStatsResponse> {
    return fetcher.get<ShopModuleReportStatsResponse>(
      `${shopReportsBaseUrl(shopId)}/module-stats`,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop settlement (P7)
// ---------------------------------------------------------------------------

function shopSettlementBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/settlement`;
}

export type ShopSettlementWalletRow = {
  id: string;
  currency: ShopCurrency;
  status: "active" | "frozen" | "closed";
};

export type ShopSettlementSummaryResponse = {
  byCurrency: Record<
    ShopCurrency,
    {
      pending: number;
      processing: number;
      paid_out: number;
      failed: number;
    }
  >;
  marketplace?: {
    byCurrency: Record<
      ShopCurrency,
      {
        pending: number;
        settled: number;
        paid_out: number;
        failed: number;
      }
    >;
  };
  wallets: ShopSettlementWalletRow[];
};

export type ShopMarketplaceSettlementRow = {
  id: string;
  orderId: string;
  shopId: string;
  grossAmount: number;
  commissionAmount: number;
  netAmount: number;
  currency: string;
  status: string;
  payoutRequestId: string | null;
  createdAt: string;
  settledAt: string | null;
};

export type ShopSettlementPeriodRow = {
  id: string;
  shopId: string;
  totalAmount: number;
  currency: string;
  status: string;
  payoutRequestId: string | null;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
  settledAt: string | null;
};

export type ShopPayoutRequestRow = {
  id: string;
  walletId: string;
  amount: number;
  currency: ShopCurrency;
  status: string;
  payoutMethod: string;
  payoutDestination: unknown;
  createdAt: string;
  updatedAt: string;
};

export const shopSettlementApi = {
  summary(shopId: string): Promise<ShopSettlementSummaryResponse> {
    return fetcher.get<ShopSettlementSummaryResponse>(
      `${shopSettlementBaseUrl(shopId)}/summary`,
    );
  },

  listPeriods(
    shopId: string,
    query: ShopSettlementPeriodsQueryDTO,
  ): Promise<{
    data: ShopSettlementPeriodRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(`${shopSettlementBaseUrl(shopId)}/periods`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    return fetcher.get(url.toString());
  },

  listMarketplacePeriods(
    shopId: string,
    query: ShopSettlementPeriodsQueryDTO,
  ): Promise<{
    data: ShopMarketplaceSettlementRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(`${shopSettlementBaseUrl(shopId)}/marketplace/periods`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    return fetcher.get(url.toString());
  },

  getPeriod(
    shopId: string,
    id: string,
  ): Promise<{
    id: string;
    shopId: string;
    totalAmount: number;
    currency: string;
    status: string;
    payoutRequestId: string | null;
    periodStart: string;
    periodEnd: string;
    createdAt: string;
    settledAt: string | null;
    posPaymentIds: string[];
  }> {
    return fetcher.get(
      `${shopSettlementBaseUrl(shopId)}/periods/${encodeURIComponent(id)}`,
    );
  },

  listPayoutRequests(
    shopId: string,
    query: ShopPayoutRequestsQueryDTO,
  ): Promise<{
    data: ShopPayoutRequestRow[];
    meta: { total: number; limit: number; offset: number };
  }> {
    const url = new URL(`${shopSettlementBaseUrl(shopId)}/payout-requests`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.status) url.searchParams.set("status", query.status);
    if (query.currency) url.searchParams.set("currency", query.currency);
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    return fetcher.get(url.toString());
  },

  createPayoutRequest(
    shopId: string,
    body: CreateShopPayoutRequestDTO,
  ): Promise<{ id: string }> {
    return fetcher.post(
      `${shopSettlementBaseUrl(shopId)}/payout-requests`,
      body,
    );
  },
};

// ---------------------------------------------------------------------------
// API — Shop inventory (Phase E)
// ---------------------------------------------------------------------------

function shopInventoryBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/inventory`;
}

export type ShopInventoryStockRow = {
  /** ຄີສະເພາະແຖວ (SKU + ສາຂາ) — ສຳລັບຕາຕະລາງ */
  listRowKey: string;
  shopProductSkuId: string;
  trackingMode: string;
  branchId: string | null;
  branchName: string | null;
  productName: string;
  categoryId: string | null;
  categoryName: string | null;
  brandName: string | null;
  shopSku: string;
  barcode: string | null;
  imageUrl: string | null;
  quantityOnHand: number | null;
  quantityReserved: number | null;
  inventoryTracked: boolean;
  inventoryUpdatedAt: string | null;
  /** Phase W — ມີເມື່ອສົ່ງ branchId */
  locatedQty?: number | null;
  unlocatedQty?: number | null;
  locationSummary?: string | null;
};

export type ShopBranchOption = { id: string; name: string };

export type ShopInventoryStockListResult = {
  data: ShopInventoryStockRow[];
  meta: { total: number; limit: number; offset: number };
};

export type ShopInventoryAdjustResult = {
  shopProductSkuId: string;
  branchId: string;
  quantityOnHand: number;
  quantityReserved: number;
};

export type ShopInventoryGoodsReceiptResult = {
  receiptId: string;
};

export type ShopStockTransferResult = {
  transferId: string;
};

export type ShopInventorySummary = {
  totalSkus: number;
  lowStockAlerts: number;
  recentUpdates: number;
  activeBranches: number;
};

export type ShopInventoryMovementRow = {
  id: number;
  createdAt: string;
  branchId: string;
  branchName: string;
  shopProductSkuId: string;
  productName: string;
  shopSku: string;
  type: string;
  qty: number;
  referenceType: string | null;
  referenceId: string | null;
  note: string | null;
  createdBy: string | null;
  createdByName: string | null;
  lotId: string | null;
  serialId: string | null;
};

export type ShopInventoryMovementsListResult = {
  data: ShopInventoryMovementRow[];
  meta: { total: number; limit: number; offset: number };
};

export type ShopInventoryReconcileRow = {
  branchId: string;
  branchName: string;
  shopProductSkuId: string;
  productName: string;
  shopSku: string;
  trackingMode: "lot" | "lot_serial";
  onHand: number;
  derivedQty: number;
  mismatch: boolean;
};

export type ShopInventoryReconcileResult = {
  data: ShopInventoryReconcileRow[];
  summary: {
    lotMismatches: number;
    serialMismatches: number;
    rowsReturned: number;
    totalOnHandTracked: number;
    totalLotDerivedQty: number;
    totalSerialInStock: number;
    mismatchUnitDelta: number;
  };
};

export type ShopInventoryLotLabelRow = {
  id: string;
  lotNo: string;
  qty: number;
  lotScanCode: string | null;
  manufacturedDate: string | null;
  expiryDate: string | null;
};

export type ShopInventoryLotLabelsListResult = {
  items: ShopInventoryLotLabelRow[];
  meta: { total: number; limit: number; offset: number };
};

export type ShopInventorySerialsListResult = {
  items: PosSerialItem[];
  meta: { total: number; limit: number; offset: number };
};

/** Lot ທີ່ມີ serial in_stock ຢ່າງນ້ອຍ 1 — lookup ກອງ serial */
export type ShopInventoryLotWithSerialsLookupItem = {
  id: string;
  lotNo: string;
  serialCount: number;
};

export type WarehouseZoneRow = {
  id: string;
  branchId: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type WarehouseLocationRow = {
  id: string;
  branchId: string;
  zoneId: string;
  code: string;
  name: string | null;
  aisle: string | null;
  shelf: string | null;
  bin: string | null;
  scanCode: string | null;
  isDefaultReceive: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type WarehouseLayoutNodeRow = {
  id: string | null;
  kind: "zone" | "location" | "obstacle" | "label";
  refZoneId: string | null;
  refLocationId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  labelOverride: string | null;
  color: string | null;
  zIndex: number;
  label: string;
  scanCode: string | null;
  isDefaultReceive: boolean;
  occupancy: {
    skuCount: number;
    totalQty: number;
    status: "empty" | "occupied" | "default_receive";
  } | null;
};

export type WarehouseLayoutResult = {
  source: "saved" | "auto";
  layout: {
    id: string | null;
    shopId: string;
    branchId: string;
    name: string;
    floorIndex: number;
    canvasWidth: number;
    canvasHeight: number;
    gridSize: number;
    backgroundImageUrl: string | null;
    backgroundOpacity: number;
  };
  nodes: WarehouseLayoutNodeRow[];
};

export type WarehouseLayoutFloorRow = {
  id: string;
  name: string;
  floorIndex: number;
  nodeCount: number;
};

export type WarehouseLayoutFloorRef = {
  layoutId: string;
  floorIndex: number;
  name: string;
};

export type WarehouseLayoutPlacementsResult = {
  zones: Record<string, WarehouseLayoutFloorRef[]>;
  zoneNodes: Record<string, WarehouseLayoutFloorRef[]>;
  locations: Record<string, WarehouseLayoutFloorRef[]>;
};

export type InventoryAssignLocationResult = {
  shopProductSkuId: string;
  branchId: string;
  fromLocationId: string | null;
  toLocationId: string;
  qty: number;
  onHand: number;
};

export type SkuLocationBalancesResult = {
  shopProductSkuId: string;
  branchId: string;
  onHand: number;
  locatedTotal: number;
  unlocatedQty: number;
  locations: Array<{
    locationId: string;
    locationCode: string;
    locationName: string | null;
    zoneCode: string;
    zoneName: string;
    qtyOnHand: number;
  }>;
};

export type InventoryByLocationRow = {
  shopProductSkuId: string;
  qtyOnHand: number;
  productName: string;
  shopSku: string;
  barcode: string | null;
  locationCode: string;
  locationName: string | null;
};

export type InventoryByLocationListResult = {
  items: InventoryByLocationRow[];
  total: number;
  offset: number;
  limit: number;
};

export type WarehouseLocationLookupResult = {
  location: WarehouseLocationRow & {
    zoneCode: string;
    zoneName: string;
  };
  items: InventoryByLocationRow[];
};

export type UnlocatedInventoryReportRow = {
  shopProductSkuId: string;
  productName: string;
  shopSku: string;
  barcode: string | null;
  onHand: number;
  locatedQty: number;
  unlocatedQty: number;
};

export type EmptyWarehouseLocationReportRow = {
  locationId: string;
  locationCode: string;
  locationName: string | null;
  zoneCode: string;
  zoneName: string;
  isDefaultReceive: boolean;
};

export type ReceiveByLocationReportRow = {
  lineId: string;
  goodsReceiptId: string;
  receivedAt: string;
  branchId: string;
  branchName: string;
  purchaseOrderId: string | null;
  poNumber: string | null;
  warehouseLocationId: string | null;
  locationCode: string | null;
  shopProductSkuId: string;
  productName: string;
  shopSku: string;
  qtyReceived: number;
};

export type WarehouseLocationReportListResult<T> = {
  data: T[];
  meta: { total: number; limit: number; offset: number };
};

export const shopInventoryApi = {
  getSummary(shopId: string): Promise<ShopInventorySummary> {
    return fetcher.get<ShopInventorySummary>(
      `${shopInventoryBaseUrl(shopId)}/summary`,
    );
  },

  listMovements(
    shopId: string,
    query: ShopInventoryMovementsQueryDTO,
  ): Promise<ShopInventoryMovementsListResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/movements`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.shopProductSkuId)
      url.searchParams.set("shopProductSkuId", query.shopProductSkuId);
    if (query.type) url.searchParams.set("type", query.type);
    if (query.createdFrom)
      url.searchParams.set("createdFrom", query.createdFrom);
    if (query.createdTo) url.searchParams.set("createdTo", query.createdTo);
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ShopInventoryMovementsListResult>(url.toString());
  },

  getReconcile(
    shopId: string,
    query: ShopInventoryReconcileQueryDTO,
  ): Promise<ShopInventoryReconcileResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/reconcile`);
    url.searchParams.set("limit", String(query.limit ?? 100));
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.shopProductSkuId)
      url.searchParams.set("shopProductSkuId", query.shopProductSkuId);
    url.searchParams.set("onlyMismatch", String(query.onlyMismatch));
    return fetcher.get<ShopInventoryReconcileResult>(url.toString());
  },

  listInventorySerials(
    shopId: string,
    query: ShopInventorySerialsQueryDTO,
  ): Promise<ShopInventorySerialsListResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/serials`);
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("shopProductSkuId", query.shopProductSkuId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.lotId) url.searchParams.set("lotId", query.lotId);
    return fetcher.get<ShopInventorySerialsListResult>(url.toString());
  },

  listLotLabels(
    shopId: string,
    query: ShopInventoryLotLabelsQueryDTO,
  ): Promise<ShopInventoryLotLabelsListResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/lots/labels`);
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("shopProductSkuId", query.shopProductSkuId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get<ShopInventoryLotLabelsListResult>(url.toString());
  },

  patchLotScanCode(
    shopId: string,
    lotId: string,
    body: { lotScanCode?: string | null; regenerate?: boolean },
  ): Promise<{ lotId: string; lotScanCode: string | null }> {
    return fetcher.patch<{ lotId: string; lotScanCode: string | null }>(
      `${shopInventoryBaseUrl(shopId)}/lots/${lotId}/scan-code`,
      body,
    );
  },

  listStock(
    shopId: string,
    query: ShopInventoryListQueryDTO,
  ): Promise<ShopInventoryStockListResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/stock`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q) url.searchParams.set("q", query.q);
    if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.shopProductSkuId)
      url.searchParams.set("shopProductSkuId", query.shopProductSkuId);
    if (query.locationId) url.searchParams.set("locationId", query.locationId);
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<ShopInventoryStockListResult>(url.toString());
  },

  adjust(
    shopId: string,
    body: ShopInventoryAdjustDTO,
  ): Promise<ShopInventoryAdjustResult> {
    return fetcher.post<ShopInventoryAdjustResult>(
      `${shopInventoryBaseUrl(shopId)}/adjustments`,
      body,
    );
  },

  receiveGoods(
    shopId: string,
    body: ShopInventoryGoodsReceiptDTO,
  ): Promise<ShopInventoryGoodsReceiptResult> {
    return fetcher.post<ShopInventoryGoodsReceiptResult>(
      `${shopInventoryBaseUrl(shopId)}/receipts`,
      body,
    );
  },

  createStockTransfer(
    shopId: string,
    body: ShopStockTransferDTO,
  ): Promise<ShopStockTransferResult> {
    return fetcher.post<ShopStockTransferResult>(
      `${shopInventoryBaseUrl(shopId)}/transfers`,
      body,
    );
  },

  reserveStock(
    shopId: string,
    body: ShopInventoryReserveDTO,
  ): Promise<ShopInventoryAdjustResult> {
    return fetcher.post<ShopInventoryAdjustResult>(
      `${shopInventoryBaseUrl(shopId)}/reserve`,
      body,
    );
  },

  releaseStock(
    shopId: string,
    body: ShopInventoryReleaseDTO,
  ): Promise<ShopInventoryAdjustResult> {
    return fetcher.post<ShopInventoryAdjustResult>(
      `${shopInventoryBaseUrl(shopId)}/release`,
      body,
    );
  },

  lookupBranches(
    shopId: string,
    params: { page: number; search?: string; limit?: number },
  ): Promise<{ items: ShopBranchOption[]; nextPage: number | null }> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/branches/lookup`);
    url.searchParams.set("page", String(params.page));
    url.searchParams.set("limit", String(params.limit ?? 20));
    if (params.search) url.searchParams.set("search", params.search);
    return fetcher.get(url.toString());
  },

  getBranch(
    shopId: string,
    branchId: string,
  ): Promise<ShopBranchOption | null> {
    return fetcher.get<ShopBranchOption | null>(
      `${shopInventoryBaseUrl(shopId)}/branches/${branchId}`,
    );
  },

  lookupLotsWithSerialsForSku(
    shopId: string,
    params: {
      branchId: string;
      shopProductSkuId: string;
      page: number;
      search?: string;
      limit?: number;
    },
  ): Promise<{
    items: ShopInventoryLotWithSerialsLookupItem[];
    nextPage: number | null;
  }> {
    const url = new URL(
      `${shopInventoryBaseUrl(shopId)}/lots/with-serials-lookup`,
    );
    url.searchParams.set("branchId", params.branchId);
    url.searchParams.set("shopProductSkuId", params.shopProductSkuId);
    url.searchParams.set("page", String(params.page));
    url.searchParams.set("limit", String(params.limit ?? 20));
    if (params.search) url.searchParams.set("search", params.search);
    return fetcher.get(url.toString());
  },

  getLotForSerialFilter(
    shopId: string,
    lotId: string,
    q: { branchId: string; shopProductSkuId: string },
  ): Promise<ShopInventoryLotWithSerialsLookupItem | null> {
    const url = new URL(
      `${shopInventoryBaseUrl(shopId)}/lots/${lotId}/serial-filter`,
    );
    url.searchParams.set("branchId", q.branchId);
    url.searchParams.set("shopProductSkuId", q.shopProductSkuId);
    return fetcher.get<ShopInventoryLotWithSerialsLookupItem | null>(
      url.toString(),
    );
  },

  listWarehouseLayoutFloors(
    shopId: string,
    branchId: string,
  ): Promise<{ items: WarehouseLayoutFloorRow[] }> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/layout/floors`);
    url.searchParams.set("branchId", branchId);
    return fetcher.get(url.toString());
  },

  listWarehouseLayoutPlacements(
    shopId: string,
    branchId: string,
  ): Promise<WarehouseLayoutPlacementsResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/layout/placements`);
    url.searchParams.set("branchId", branchId);
    return fetcher.get(url.toString());
  },

  assignWarehouseZoneFloor(
    shopId: string,
    body: WarehouseLayoutAssignZoneDTO,
  ): Promise<{
    zoneId: string;
    layoutId: string | null;
    placements: WarehouseLayoutPlacementsResult;
  }> {
    return fetcher.post(
      `${shopInventoryBaseUrl(shopId)}/layout/assign-zone`,
      body,
    );
  },

  createWarehouseLayoutFloor(
    shopId: string,
    body: WarehouseLayoutFloorCreateDTO,
  ): Promise<{
    id: string;
    name: string;
    floorIndex: number;
    branchId: string;
    shopId: string;
  }> {
    return fetcher.post(`${shopInventoryBaseUrl(shopId)}/layout/floors`, body);
  },

  getWarehouseLayout(
    shopId: string,
    branchId: string,
    opts?: { layoutId?: string; floorIndex?: number },
  ): Promise<WarehouseLayoutResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/layout`);
    url.searchParams.set("branchId", branchId);
    if (opts?.layoutId) url.searchParams.set("layoutId", opts.layoutId);
    if (opts?.floorIndex !== undefined) {
      url.searchParams.set("floorIndex", String(opts.floorIndex));
    }
    return fetcher.get(url.toString());
  },

  upsertWarehouseLayout(
    shopId: string,
    body: WarehouseLayoutUpsertDTO,
  ): Promise<Record<string, unknown>> {
    return fetcher.put(`${shopInventoryBaseUrl(shopId)}/layout`, body);
  },

  replaceWarehouseLayoutNodes(
    shopId: string,
    body: WarehouseLayoutNodesReplaceDTO,
  ): Promise<WarehouseLayoutResult> {
    return fetcher.put(`${shopInventoryBaseUrl(shopId)}/layout/nodes`, body);
  },

  generateWarehouseLayout(
    shopId: string,
    body: WarehouseLayoutGenerateDTO,
  ): Promise<WarehouseLayoutResult> {
    return fetcher.post(
      `${shopInventoryBaseUrl(shopId)}/layout/generate`,
      body,
    );
  },

  setWarehouseLayoutBackground(
    shopId: string,
    body: WarehouseLayoutBackgroundDTO,
  ): Promise<WarehouseLayoutResult> {
    return fetcher.post(
      `${shopInventoryBaseUrl(shopId)}/layout/background`,
      body,
    );
  },

  deleteWarehouseLayout(
    shopId: string,
    branchId: string,
    layoutId?: string,
  ): Promise<{ ok: true }> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/layout`);
    url.searchParams.set("branchId", branchId);
    if (layoutId) url.searchParams.set("layoutId", layoutId);
    return fetcher.delete(url.toString());
  },

  listWarehouseZones(
    shopId: string,
    branchId: string,
  ): Promise<{ items: WarehouseZoneRow[] }> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/zones`);
    url.searchParams.set("branchId", branchId);
    return fetcher.get(url.toString());
  },

  createWarehouseZone(
    shopId: string,
    body: WarehouseZoneCreateDTO,
  ): Promise<WarehouseZoneRow> {
    return fetcher.post(`${shopInventoryBaseUrl(shopId)}/zones`, body);
  },

  updateWarehouseZone(
    shopId: string,
    zoneId: string,
    body: WarehouseZoneUpdateDTO,
  ): Promise<WarehouseZoneRow> {
    return fetcher.patch(
      `${shopInventoryBaseUrl(shopId)}/zones/${zoneId}`,
      body,
    );
  },

  deleteWarehouseZone(shopId: string, zoneId: string): Promise<{ ok: true }> {
    return fetcher.delete(`${shopInventoryBaseUrl(shopId)}/zones/${zoneId}`);
  },

  listWarehouseLocations(
    shopId: string,
    query: { branchId: string; zoneId?: string },
  ): Promise<{ items: WarehouseLocationRow[] }> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/locations`);
    url.searchParams.set("branchId", query.branchId);
    if (query.zoneId) url.searchParams.set("zoneId", query.zoneId);
    return fetcher.get(url.toString());
  },

  createWarehouseLocation(
    shopId: string,
    body: WarehouseLocationCreateDTO,
  ): Promise<WarehouseLocationRow> {
    return fetcher.post(`${shopInventoryBaseUrl(shopId)}/locations`, body);
  },

  updateWarehouseLocation(
    shopId: string,
    locationId: string,
    body: WarehouseLocationUpdateDTO,
  ): Promise<WarehouseLocationRow> {
    return fetcher.patch(
      `${shopInventoryBaseUrl(shopId)}/locations/${locationId}`,
      body,
    );
  },

  deleteWarehouseLocation(
    shopId: string,
    locationId: string,
  ): Promise<{ ok: true }> {
    return fetcher.delete(
      `${shopInventoryBaseUrl(shopId)}/locations/${locationId}`,
    );
  },

  assignInventoryLocation(
    shopId: string,
    body: InventoryAssignLocationDTO,
  ): Promise<InventoryAssignLocationResult> {
    return fetcher.post(
      `${shopInventoryBaseUrl(shopId)}/assign-location`,
      body,
    );
  },

  listInventoryByLocation(
    shopId: string,
    query: InventoryByLocationQueryDTO,
  ): Promise<InventoryByLocationListResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/by-location`);
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("locationId", query.locationId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q) url.searchParams.set("q", query.q);
    return fetcher.get(url.toString());
  },

  listSkuLocationBalances(
    shopId: string,
    shopProductSkuId: string,
    branchId: string,
  ): Promise<SkuLocationBalancesResult> {
    const url = new URL(
      `${shopInventoryBaseUrl(shopId)}/sku/${shopProductSkuId}/locations`,
    );
    url.searchParams.set("branchId", branchId);
    return fetcher.get(url.toString());
  },

  lookupWarehouseLocationByScan(
    shopId: string,
    scanCode: string,
  ): Promise<WarehouseLocationLookupResult> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/locations/lookup`);
    url.searchParams.set("scanCode", scanCode);
    return fetcher.get(url.toString());
  },

  listUnlocatedInventoryReport(
    shopId: string,
    query: WarehouseUnlocatedReportQueryDTO,
  ): Promise<WarehouseLocationReportListResult<UnlocatedInventoryReportRow>> {
    const url = new URL(`${shopInventoryBaseUrl(shopId)}/reports/unlocated`);
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.q) url.searchParams.set("q", query.q);
    return fetcher.get(url.toString());
  },

  listEmptyWarehouseLocationsReport(
    shopId: string,
    query: WarehouseEmptyLocationsReportQueryDTO,
  ): Promise<
    WarehouseLocationReportListResult<EmptyWarehouseLocationReportRow>
  > {
    const url = new URL(
      `${shopInventoryBaseUrl(shopId)}/reports/empty-locations`,
    );
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get(url.toString());
  },

  listReceiveByLocationReport(
    shopId: string,
    query: WarehouseReceiveByLocationReportQueryDTO,
  ): Promise<WarehouseLocationReportListResult<ReceiveByLocationReportRow>> {
    const url = new URL(
      `${shopInventoryBaseUrl(shopId)}/reports/receive-by-location`,
    );
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    if (query.locationId) url.searchParams.set("locationId", query.locationId);
    if (query.receivedFrom)
      url.searchParams.set("receivedFrom", query.receivedFrom);
    if (query.receivedTo) url.searchParams.set("receivedTo", query.receivedTo);
    return fetcher.get(url.toString());
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** เช็คว่า role มี permission นั้นในรายการหรือไม่ (ไม่รวมระดับยืนยันร้าน) */
export function hasMerchantPermission(
  permissions: string[],
  permissionId: string,
): boolean {
  return permissions.includes(permissionId);
}

/**
 * เช็คทั้ง "มีสิทธิ์จาก role" และ "ร้านยืนยันเพียงพอ"
 * ใช้เมื่อจะให้ใช้ฟีเจอร์ได้จริง (เช่น RequireMerchantPermission)
 */
export function canUseMerchantPermission(
  permissions: string[],
  permissionId: string,
  verifiedLevel: ShopVerifiedLevel | null | undefined,
): boolean {
  return (
    hasMerchantPermission(permissions, permissionId) &&
    canUsePermissionWithLevel(
      permissionId as MerchantPermissionId,
      verifiedLevel,
    )
  );
}

export type { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
